/**
 * LineageGraph
 *
 * SVG visualization of an item's direct **provenance** relationships (depth-1):
 * items that produced the focal item appear above it; items the focal item
 * produced appear below. Data source: `lineage_events` (identity birth/death
 * operations per §0A — not the full Lineage journey).
 *
 * Uses dagre for automatic TB layout. Edge curves are computed from node
 * positions so the result is clean and predictable.
 * Theme-aware: light and dark palettes via useTheme() for SVG nodes, edges, and chrome.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import dagre from '@dagrejs/dagre';
import type { LineageEvent, LineageEventType } from '../../../lib/lineageService';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { GitMerge } from 'lucide-react';
import { VisualSurface } from '../visual';
import { useTheme } from '../../../contexts/ThemeContext';

// ============================================================================
// Layout constants
// ============================================================================

const NODE_W = 184;
const NODE_H = 54;
const GHOST_W = 96;
const GHOST_H = 32;
const PAD = 48;

// ============================================================================
// Event type styling
// ============================================================================

interface EventStyle {
  stroke: string;
  fill: string;
  textColor: string;
  label: string;
}

const EVENT_STYLE_LIGHT: Record<LineageEventType, EventStyle> = {
  retire:    { stroke: '#94a3b8', fill: '#f8fafc', textColor: '#64748b', label: 'Retired'    },
  supersede: { stroke: '#3b82f6', fill: '#eff6ff', textColor: '#2563eb', label: 'Superseded' },
  split:     { stroke: '#f97316', fill: '#fff7ed', textColor: '#ea580c', label: 'Split'      },
  merge:     { stroke: '#10b981', fill: '#ecfdf5', textColor: '#059669', label: 'Merged'     },
  fork:      { stroke: '#8b5cf6', fill: '#f5f3ff', textColor: '#7c3aed', label: 'Forked'     },
};

const EVENT_STYLE_DARK: Record<LineageEventType, EventStyle> = {
  retire:    { stroke: '#64748b', fill: '#1e293b', textColor: '#94a3b8', label: 'Retired'    },
  supersede: { stroke: '#60a5fa', fill: '#172554', textColor: '#93c5fd', label: 'Superseded' },
  split:     { stroke: '#fb923c', fill: '#431407', textColor: '#fdba74', label: 'Split'      },
  merge:     { stroke: '#34d399', fill: '#064e3b', textColor: '#6ee7b7', label: 'Merged'     },
  fork:      { stroke: '#a78bfa', fill: '#2e1065', textColor: '#c4b5fd', label: 'Forked'     },
};

function getEventStyles(isDark: boolean): Record<LineageEventType, EventStyle> {
  return isDark ? EVENT_STYLE_DARK : EVENT_STYLE_LIGHT;
}

interface NodePalette {
  ghostFill: string;
  ghostStroke: string;
  ghostText: string;
  focalFill: string;
  focalStroke: string;
  focalInnerStroke: string;
  focalHoverFill: string;
  normalFill: string;
  normalStroke: string;
  normalHoverFill: string;
  hoverStroke: string;
  textFocal: string;
  textNormal: string;
  subColor: string;
}

const NODE_PALETTE_LIGHT: NodePalette = {
  ghostFill: '#f1f5f9',
  ghostStroke: '#cbd5e1',
  ghostText: '#94a3b8',
  focalFill: '#f3f0ff',
  focalStroke: '#7c3aed',
  focalInnerStroke: '#a78bfa',
  focalHoverFill: '#ede9fe',
  normalFill: '#ffffff',
  normalStroke: '#e2e8f0',
  normalHoverFill: '#faf5ff',
  hoverStroke: '#c084fc',
  textFocal: '#5b21b6',
  textNormal: '#1e293b',
  subColor: '#94a3b8',
};

const NODE_PALETTE_DARK: NodePalette = {
  ghostFill: '#1a1d24',
  ghostStroke: '#3a3f4a',
  ghostText: '#636880',
  focalFill: '#2e1065',
  focalStroke: '#8b5cf6',
  focalInnerStroke: '#a78bfa',
  focalHoverFill: '#3b0764',
  normalFill: '#22262f',
  normalStroke: '#3a3f4a',
  normalHoverFill: '#2a2540',
  hoverStroke: '#a78bfa',
  textFocal: '#ddd6fe',
  textNormal: '#e8eaf0',
  subColor: '#7a8094',
};

function getNodePalette(isDark: boolean): NodePalette {
  return isDark ? NODE_PALETTE_DARK : NODE_PALETTE_LIGHT;
}

// ============================================================================
// Graph types
// ============================================================================

interface LayoutNode {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  isFocal: boolean;
  isGhost: boolean; // "Retired" placeholder when event_type=retire has no targets
  ghostLabel?: string;
}

interface LayoutEdge {
  id: string;
  sourceId: string;
  targetId: string;
  eventType: LineageEventType;
  // Computed SVG path
  path: string;
  labelX: number;
  labelY: number;
  markerColor: string;
  label: string;
}

// ============================================================================
// Graph builder
// ============================================================================

function buildLayout(
  focalItemId: string,
  events: LineageEvent[],
  getTitle: (id: string) => string | undefined,
): { nodes: LayoutNode[]; edges: LayoutEdge[]; svgW: number; svgH: number } {
  const itemIds = new Set<string>([focalItemId]);
  for (const ev of events) {
    for (const id of ev.sourceItemIds) itemIds.add(id);
    for (const id of ev.targetItemIds) itemIds.add(id);
  }

  // Ghost IDs for retire events (no targets)
  const ghostNodes: Map<string, { label: string }> = new Map();
  for (const ev of events) {
    if (ev.eventType === 'retire' && ev.targetItemIds.length === 0) {
      const ghostId = `__ghost_${ev.id}`;
      ghostNodes.set(ghostId, { label: 'Retired' });
    }
  }

  const g = new dagre.graphlib.Graph({ directed: true, multigraph: true });
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 64, ranksep: 96, marginx: PAD, marginy: PAD });

  for (const id of itemIds) {
    g.setNode(id, { width: NODE_W, height: NODE_H });
  }
  for (const [ghostId] of ghostNodes) {
    g.setNode(ghostId, { width: GHOST_W, height: GHOST_H });
  }

  // Deduplicate edges for multigraph (dagre needs unique names per edge pair)
  const edgeCounter = new Map<string, number>();
  const rawEdges: Array<{ src: string; tgt: string; key: string; eventType: LineageEventType; eventId: string }> = [];

  for (const ev of events) {
    const targets =
      ev.eventType === 'retire' && ev.targetItemIds.length === 0
        ? [`__ghost_${ev.id}`]
        : ev.targetItemIds;

    for (const src of ev.sourceItemIds) {
      for (const tgt of targets) {
        const pairKey = `${src}→${tgt}`;
        const count = (edgeCounter.get(pairKey) ?? 0) + 1;
        edgeCounter.set(pairKey, count);
        const key = `${pairKey}#${count}`;
        rawEdges.push({ src, tgt, key, eventType: ev.eventType, eventId: ev.id });
        g.setEdge(src, tgt, {}, key);
      }
    }
  }

  dagre.layout(g);

  const gMeta = g.graph();
  const svgW = (gMeta.width ?? 400) + PAD * 2;
  const svgH = (gMeta.height ?? 300) + PAD * 2;

  // Build nodes
  const nodes: LayoutNode[] = [];
  for (const id of g.nodes()) {
    const n = g.node(id);
    if (!n) continue;
    const isGhost = id.startsWith('__ghost_');
    nodes.push({
      id,
      x: n.x,
      y: n.y,
      w: n.width as number,
      h: n.height as number,
      title: isGhost ? (ghostNodes.get(id)?.label ?? 'Retired') : (getTitle(id) ?? id.slice(0, 8) + '…'),
      isFocal: id === focalItemId,
      isGhost,
      ghostLabel: isGhost ? ghostNodes.get(id)?.label : undefined,
    });
  }

  // Build edges
  const edges: LayoutEdge[] = [];
  for (const re of rawEdges) {
    const srcNode = g.node(re.src);
    const tgtNode = g.node(re.tgt);
    if (!srcNode || !tgtNode) continue;

    const x0 = srcNode.x;
    const y0 = srcNode.y + (srcNode.height as number) / 2;
    const x1 = tgtNode.x;
    const y1 = tgtNode.y - (tgtNode.height as number) / 2;
    const cp = (y1 - y0) * 0.45;

    const path = `M ${x0} ${y0} C ${x0} ${y0 + cp}, ${x1} ${y1 - cp}, ${x1} ${y1}`;
    const labelX = (x0 + x1) / 2;
    const labelY = (y0 + y1) / 2;

    const style = EVENT_STYLE_LIGHT[re.eventType] ?? EVENT_STYLE_LIGHT.retire;
    edges.push({
      id: `${re.eventId}-${re.src}-${re.tgt}`,
      sourceId: re.src,
      targetId: re.tgt,
      eventType: re.eventType,
      path,
      labelX,
      labelY,
      markerColor: style.stroke,
      label: style.label,
    });
  }

  return { nodes, edges, svgW, svgH };
}

// ============================================================================
// Arrow marker def (one per event color)
// ============================================================================

function ArrowDefs({ isDark }: { isDark: boolean }) {
  const styles = getEventStyles(isDark);
  return (
    <defs>
      {(Object.entries(styles) as Array<[LineageEventType, EventStyle]>).map(([type, s]) => (
        <marker
          key={type}
          id={`arrow-${type}`}
          markerWidth="8"
          markerHeight="8"
          refX="6"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,6 L8,3 z" fill={s.stroke} />
        </marker>
      ))}
    </defs>
  );
}

// ============================================================================
// Node SVG element
// ============================================================================

function GraphNodeEl({ node, isDark }: { node: LayoutNode; isDark: boolean }) {
  const palette = getNodePalette(isDark);
  const halfW = node.w / 2;
  const halfH = node.h / 2;
  const x = node.x - halfW;
  const y = node.y - halfH;

  if (node.isGhost) {
    return (
      <g>
        <rect
          x={x} y={y} width={node.w} height={node.h}
          rx={4}
          fill={palette.ghostFill}
          stroke={palette.ghostStroke}
          strokeWidth={1}
          strokeDasharray="4 3"
        />
        <text
          x={node.x} y={node.y + 1}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={11}
          fill={palette.ghostText}
          fontFamily="inherit"
        >
          {node.ghostLabel ?? 'Retired'}
        </text>
      </g>
    );
  }

  const fill = node.isFocal ? palette.focalFill : palette.normalFill;
  const hoverFill = node.isFocal ? palette.focalHoverFill : palette.normalHoverFill;
  const stroke = node.isFocal ? palette.focalStroke : palette.normalStroke;
  const strokeW = node.isFocal ? 2 : 1.5;
  const textColor = node.isFocal ? palette.textFocal : palette.textNormal;

  // Truncate title to ~22 chars per line
  const title = node.title;
  const MAX = 22;
  const line1 = title.length > MAX ? title.slice(0, MAX) + '…' : title;

  return (
    <Link to={`/prioritization/items/${node.id}`} style={{ textDecoration: 'none' }}>
      <g className="cursor-pointer group">
        <rect
          x={x} y={y} width={node.w} height={node.h}
          rx={8}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeW}
          className="transition-colors"
        />
        <rect
          x={x} y={y} width={node.w} height={node.h}
          rx={8}
          fill={hoverFill}
          stroke={palette.hoverStroke}
          strokeWidth={strokeW}
          className="opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        />
        {node.isFocal && (
          <rect
            x={x + 0.5} y={y + 0.5}
            width={node.w - 1} height={node.h - 1}
            rx={7.5}
            fill="none"
            stroke={palette.focalInnerStroke}
            strokeWidth={0.75}
            className="pointer-events-none"
          />
        )}
        <text
          x={node.x} y={node.y - (node.h / 2 > 28 ? 6 : 2)}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={12}
          fontWeight={node.isFocal ? '600' : '500'}
          fill={textColor}
          fontFamily="inherit"
          className="pointer-events-none"
        >
          {line1}
        </text>
        {node.isFocal && (
          <text
            x={node.x} y={node.y + 12}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={9.5}
            fill={palette.subColor}
            fontFamily="inherit"
            letterSpacing="0.04em"
            className="pointer-events-none"
          >
            THIS ITEM
          </text>
        )}
      </g>
    </Link>
  );
}

// ============================================================================
// Edge SVG element
// ============================================================================

function GraphEdgeEl({ edge, isDark }: { edge: LayoutEdge; isDark: boolean }) {
  const style = getEventStyles(isDark)[edge.eventType] ?? getEventStyles(isDark).retire;
  return (
    <g>
      <path
        d={edge.path}
        fill="none"
        stroke={style.stroke}
        strokeWidth={1.75}
        markerEnd={`url(#arrow-${edge.eventType})`}
        opacity={0.85}
      />
      {/* Edge label chip */}
      <rect
        x={edge.labelX - 26}
        y={edge.labelY - 9}
        width={52}
        height={18}
        rx={9}
        fill={style.fill}
        stroke={style.stroke}
        strokeWidth={0.75}
      />
      <text
        x={edge.labelX}
        y={edge.labelY + 0.5}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={9.5}
        fontWeight="600"
        fill={style.textColor}
        fontFamily="inherit"
        letterSpacing="0.03em"
      >
        {edge.label}
      </text>
    </g>
  );
}

// ============================================================================
// Legend
// ============================================================================

function Legend({ usedTypes, isDark }: { usedTypes: Set<LineageEventType>; isDark: boolean }) {
  const styles = getEventStyles(isDark);
  const entries = (Object.entries(styles) as Array<[LineageEventType, EventStyle]>).filter(
    ([t]) => usedTypes.has(t),
  );
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 px-1 pt-2">
      {entries.map(([type, s]) => (
        <div key={type} className="flex items-center gap-1.5">
          <div
            className="w-3 h-1.5 rounded-full"
            style={{ backgroundColor: s.stroke }}
          />
          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Phone fallback — provenance list (VisualSurface)
// ============================================================================

function LineageGraphPhoneList({
  events,
  itemId,
  getTitle,
  isDark,
}: {
  events: LineageEvent[];
  itemId: string;
  getTitle: (id: string) => string | undefined;
  isDark: boolean;
}) {
  const styles = getEventStyles(isDark);
  return (
    <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
      {events.map((ev) => {
        const style = styles[ev.eventType] ?? styles.retire;
        const sources = ev.sourceItemIds
          .map((id) => getTitle(id) ?? `${id.slice(0, 8)}…`)
          .join(', ');
        const targets =
          ev.targetItemIds.length > 0
            ? ev.targetItemIds.map((id) => getTitle(id) ?? `${id.slice(0, 8)}…`).join(', ')
            : 'Retired';
        const role = ev.sourceItemIds.includes(itemId) ? 'source' : 'target';

        return (
          <div key={ev.id} className="px-4 py-3 space-y-1">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                style={{ backgroundColor: style.fill, color: style.textColor }}
              >
                {style.label}
              </span>
              {role === 'source' && (
                <span className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">this item →</span>
              )}
              {role === 'target' && (
                <span className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">→ this item</span>
              )}
            </div>
            <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {sources || '—'} → {targets}
            </p>
            {ev.rationale && (
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{ev.rationale}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

interface LineageGraphProps {
  itemId: string;
  events: LineageEvent[];
}

export function LineageGraph({ itemId, events }: LineageGraphProps) {
  const { isDark } = useTheme();
  const getItemById = useItemsStore((s) => s.getItemById);
  const getTitle = (id: string) => getItemById(id)?.title;

  const { nodes, edges, svgW, svgH } = useMemo(
    () => buildLayout(itemId, events, getTitle),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemId, events],
  );

  const usedTypes = useMemo(
    () => new Set(events.map((e) => e.eventType)),
    [events],
  );

  if (events.length === 0) return null;

  return (
    <VisualSurface
      title="Lineage Graph"
      subtitle="direct ancestry + derivatives"
      icon={<GitMerge className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />}
      legend={<Legend usedTypes={usedTypes} isDark={isDark} />}
      fallback={
        <LineageGraphPhoneList events={events} itemId={itemId} getTitle={getTitle} isDark={isDark} />
      }
    >
      <svg
        width={svgW}
        height={svgH}
        style={{ display: 'block', minWidth: '100%' }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <ArrowDefs isDark={isDark} />
        <g>{edges.map((e) => <GraphEdgeEl key={e.id} edge={e} isDark={isDark} />)}</g>
        <g>{nodes.map((n) => <GraphNodeEl key={n.id} node={n} isDark={isDark} />)}</g>
      </svg>
    </VisualSurface>
  );
}
